import React from 'react';
import { Col, Container } from '@zg-rentals/zrs-comet';
import { Button, Flex } from '@zillow/constellation';
import { StyledFilterControlsContainer, StyledFilterControlsRow, StyledFilterPillsContainer } from './StyledComponents';
import type { AdvancedTableFilterConfig, AdvancedTableFilterOption } from './AdvancedTable';
import MultiSelectFilterPill from './filters/MultiSelectFilterPill';
import DateFilterPill from './filters/DateFilterPill';

interface BillingFilterProps {
  handleFilterToggle: () => void;
  handleSaveFilters: (filters: AdvancedTableFilterConfig) => void;
  initialFilters: AdvancedTableFilterConfig;
  isFilterOpen: boolean;
}

const BillingFilter = ({
  handleFilterToggle,
  handleSaveFilters,
  initialFilters = {},
  isFilterOpen = false,
}: BillingFilterProps) => {
  const [filters, setFilters] = React.useState(initialFilters);
  const wrapperRef = React.useRef(null);
  const lastFilterState = React.useRef(false);
  const controlsRef = React.useRef(null);
  if (Object.values(initialFilters).length === 0) return null;

  // Update the filters before closing or opening
  if (isFilterOpen !== lastFilterState.current) {
    lastFilterState.current = isFilterOpen;
    setFilters(initialFilters);
  }

  // @ts-ignore
  const updateFilters = (key, value, save) => {
    const newFilters = {
      ...filters,
      [key]: {
        // @ts-ignore
        ...filters[key],
        value,
      },
    };
    setFilters(newFilters);
    if (save) handleSaveFilters(newFilters);
  };

  const renderPills = () => {
    const pills = [];
    for (const [key, filter] of Object.entries(filters)) {
      if (!filter) continue;
      if (filter.type === 'MULTISELECT') {
        pills.push(
          <MultiSelectFilterPill
            key={key}
            isFilterOpen={isFilterOpen}
            value={filter.value}
            title={filter.title}
            handleChange={(value: Array<AdvancedTableFilterOption>) => updateFilters(key, value, false)}
            handleClearFilter={(value: Array<AdvancedTableFilterOption>) => updateFilters(key, value, true)}
          />,
        );
      } else if (filter.type === 'DATE') {
        pills.push(
          <DateFilterPill
            key={key}
            keyName={key}
            isFilterOpen={isFilterOpen}
            value={filter.value}
            title={filter.title}
            handleChange={(value: string | number | Date) => updateFilters(key, value, false)}
            handleClearFilter={(value: string | number | Date | undefined) => updateFilters(key, value, true)}
            filters={filters}
            isDateEnabled={filter.isDateEnabled}
          />,
        );
      }
    }
    return pills;
  };

  const anyFilterOpen = Object.values(filters).some((filter) => {
    if (!filter) return false;
    if (filter.type === 'MULTISELECT') {
      return filter.value.some((subValue) => subValue.isChecked);
    } else if (filter.type === 'DATE') {
      return Boolean(filter.value);
    }
    return false;
  });

  return (
    <Container>
      <div ref={wrapperRef} className="BillingFilter">
        <StyledFilterControlsContainer ref={controlsRef} isAnyFilterOpen={anyFilterOpen} isFilterOpen={isFilterOpen}>
          <StyledFilterPillsContainer isFilterOpen={isFilterOpen}>{renderPills()}</StyledFilterPillsContainer>
          {isFilterOpen && (
            <StyledFilterControlsRow>
              <Col xs={12}>
                <Flex display="flex" justifyContent="flex-end" marginY="xs">
                  <Button onClick={handleFilterToggle} buttonType="secondary">
                    Cancel
                  </Button>
                  <Button buttonType="primary" onClick={() => handleSaveFilters(filters)} marginLeft="sm">
                    Apply
                  </Button>
                </Flex>
              </Col>
            </StyledFilterControlsRow>
          )}
        </StyledFilterControlsContainer>
      </div>
    </Container>
  );
};

export default BillingFilter;
